import ConditionalRender from 'base/ConditionalRender/ConditionalRender';
import { StandardButton } from 'base/StandardButton/StandardButton';
import SearchBox from 'components/common/SearchBox';
import { FEDERAL_STATE_FORM } from 'constant/RoutesUrls';
import { t } from 'i18next';
import React, { Fragment, useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch } from '../../redux/HooksRedux';
import { RootState } from 'redux/rootReducer';
import {
  ACCENDING,
  DESCENDING,
  ENGLISH,
  modules,
  SELECTED_LANGUAGE,
  ViewAllLimit
} from 'utils/enums/misc';
import { capitalizeFirstLetter, checkAccess } from 'utils/helpers';
import { FederalStateListI, paramsI } from '../../../types/interface';
import { ColumnDef, SortingState } from '@tanstack/react-table';
import { getCookie } from 'utils/cookieUtils';
import * as federalStateAction from '../../redux/FederalState/FederalStateAction';
import {
  errorNotification,
  successNotification
} from 'base/notifications/Notifications';
import { debounceTime } from 'utils/staticData';
import useAdvanceTable from 'hooks/useAdvanceTable';
import DeleteModel from 'base/DeleteModal/DeleteModal';
import { StandardLoader } from 'base/Loader/Loader';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import AdvanceTable from 'components/base/AdvanceTable';
import CustomAdvanceTableFooter from 'components/customComponent/CustomAdvanceTableFooter';
import { Dropdown } from 'react-bootstrap';
import { UilEllipsisV, UilUsersAlt } from '@iconscout/react-unicons';
import ViewUserModal from 'components/ViewUserModal/ViewUserModal';

const FederalState = () => {
  const [searchText, setSearchText] = useState<string>('');
  const [pageCount, setPageCount] = useState<number>(1);
  const [isAllVisibleData, setIsAllVisibleData] = useState(false);
  const [limit, setLimit] = useState<number>(10);
  const [allFederalStates, setAllFederalStates] = useState<FederalStateListI[]>(
    []
  );
  const [isLoading, setIsLoading] = useState(true);
  const [sorting, setSorting] = useState<SortingState>();
  const [deleteFederalStateData, setDeleteFederalStateData] = useState<{
    state_id: number;
    showDeletePopup: boolean;
  }>({
    state_id: 0,
    showDeletePopup: false
  });
  const [viewFederalState, setViewFederalState] = useState<{
    show: boolean;
    data: FederalStateListI | undefined;
  }>({
    show: false,
    data: undefined
  });

  const userPermission = useSelector(
    (state: RootState) => state.userProfile.permission
  );
  const { totalPages, totalRecords } = useSelector(
    (state: RootState) => state.federalStateSlice || 0
  );
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const currentLanguage = getCookie(SELECTED_LANGUAGE);

  const getAllFederalStates = async () => {
    const params: paramsI = {
      page: pageCount,
      limit: isAllVisibleData ? ViewAllLimit : limit,
      keyword: searchText,
      sortBy: sorting?.[0]?.id,
      sortOrder: sorting?.[0]?.desc ? DESCENDING : ACCENDING
    };
    setIsLoading(true);
    try {
      const response = await dispatch(
        federalStateAction.getFederalStates(params)
      );
      if (response?.status === 200) {
        setAllFederalStates(response.data?.data || []);
        if (pageCount > response?.data?.pagination?.totalPages) {
          setPageCount(1);
        }
      } else {
        errorNotification(response?.data?.message);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = (stateId: number) => {
    navigate(`${FEDERAL_STATE_FORM}?editFederalStateId=${stateId?.toString()}`);
  };

  const debouncedSearch = useCallback(() => {
    const handler = setTimeout(() => {
      getAllFederalStates();
    }, debounceTime);

    return () => {
      clearTimeout(handler);
    };
  }, [searchText, pageCount, isAllVisibleData, sorting, limit]);

  useEffect(() => {
    setIsLoading(true);
    const cleanup = debouncedSearch();
    return cleanup;
  }, [searchText, pageCount, isAllVisibleData, sorting, limit]);

  const handleDeleteFederalState = async () => {
    try {
      const response = await dispatch(
        federalStateAction.deleteFederalState(deleteFederalStateData.state_id)
      );
      if (response?.status === 200) {
        successNotification(response.data?.message);
        setDeleteFederalStateData({ state_id: 0, showDeletePopup: false });
        getAllFederalStates();
      } else {
        errorNotification(response?.data?.message);
      }
    } catch (error) {
      setDeleteFederalStateData({ state_id: 0, showDeletePopup: false });
      console.log(error);
    }
  };

  const renderDeletePopup = () => {
    if (deleteFederalStateData.showDeletePopup) {
      return (
        <DeleteModel
          onCancel={() =>
            setDeleteFederalStateData({ state_id: 0, showDeletePopup: false })
          }
          onConfirm={handleDeleteFederalState}
        />
      );
    }
  };

  const columns: ColumnDef<FederalStateListI>[] = [
    {
      id: 'federal_state_number',
      accessorKey: t('federal_state.table.column_1'),
      enableSorting: false,
      header: () => (
        <span className="text-nowrap">{t('applications.table.title_1')}</span>
      ),
      cell: ({ row: { index } }) => <div className="py-2">{index + 1}</div>
    },
    {
      id: 'federal_state_name',
      accessorKey: t('federal_state.table.column_2'),
      enableSorting: false,
      header: () => (
        <span className="text-nowrap">{t('federal_state.table.column_2')}</span>
      ),
      cell: ({ row }) => (
        <div className="py-2">
          {currentLanguage === ENGLISH
            ? row.original?.state_name_en
            : row.original?.state_name_de || '-'}
        </div>
      )
    },
    {
      id: 'assigned_user_count',
      accessorKey: t('federal_state.table.column_4'),
      enableSorting: false,
      header: () => (
        <span className="text-nowrap">{t('federal_state.table.column_4')}</span>
      ),
      cell: ({ row }) => (
        <div className="py-2">{row.original?.assignedUserCounts || '0'}</div>
      )
    },
    {
      id: 'view',
      accessorKey: t('federal_state.table.column_5'),
      enableSorting: false,
      header: () => (
        <span className="text-nowrap">{t('federal_state.table.column_5')}</span>
      ),
      cell: ({ row }) => (
        <div className="py-2">
          <StandardButton
            buttonText={t('federal_state.table.button.text_1')}
            onClick={() =>
              setViewFederalState({ show: true, data: row.original })
            }
          />
        </div>
      )
    }
    // {
    //   id: 'action',
    //   accessorKey: checkAccess(
    //     [modules.FEDERAL_STATE.edit, modules.FEDERAL_STATE.delete],
    //     userPermission
    //   )
    //     ? t('general.common_text.text_8')
    //     : '',
    //   enableSorting: false,
    //   header: () =>
    //     checkAccess(
    //       [modules.FEDERAL_STATE.edit, modules.FEDERAL_STATE.delete],
    //       userPermission
    //     ) ? (
    //       <span className="text-nowrap">{t('general.common_text.text_8')}</span>
    //     ) : (
    //       ''
    //     ),
    //   cell: ({ row: { original } }) => (
    //     <ConditionalRender
    //       condition={checkAccess(
    //         [modules.FEDERAL_STATE.edit, modules.FEDERAL_STATE.delete],
    //         userPermission
    //       )}
    //     >
    //       <Dropdown
    //         id="scroll_in_table"
    //         placement="auto"
    //         className="position-static"
    //       >
    //         <Dropdown.Toggle
    //           variant="link"
    //           className="no-padding no-margin ps-0 d-flex"
    //         >
    //           <UilEllipsisV className="text-body" />
    //         </Dropdown.Toggle>

    //         <Dropdown.Menu className="p-0">
    //           <ConditionalRender
    //             condition={checkAccess(
    //               [modules.FEDERAL_STATE.edit],
    //               userPermission
    //             )}
    //           >
    //             <Dropdown.Item
    //               className="pe-0 border-bottom"
    //               onClick={() => handleEdit(original?.state_id)}
    //             >
    //               <span className="d-flex fs-16 p-0 font-normal">
    //                 <p className="mb-0">
    //                   {capitalizeFirstLetter(t('general.common_text.text_13'))}
    //                 </p>
    //               </span>
    //             </Dropdown.Item>
    //           </ConditionalRender>
    //           <ConditionalRender
    //             condition={checkAccess(
    //               [modules.FEDERAL_STATE.delete],
    //               userPermission
    //             )}
    //           >
    //             <Dropdown.Item
    //               onClick={() => {
    //                 setDeleteFederalStateData({
    //                   state_id: original?.state_id,
    //                   showDeletePopup: true
    //                 });
    //               }}
    //             >
    //               <span className="d-flex fs-16 font-normal p-0">
    //                 <p className="mb-0">
    //                   {capitalizeFirstLetter(t('general.common_text.text_14'))}
    //                 </p>
    //               </span>
    //             </Dropdown.Item>
    //           </ConditionalRender>
    //         </Dropdown.Menu>
    //       </Dropdown>
    //     </ConditionalRender>
    //   )
    // }
  ];

  const table = useAdvanceTable({
    data: allFederalStates || [],
    columns: columns,
    selection: false,
    sortable: true,
    pageSize: limit,
    pagination: true,
    sorting: sorting,
    setSorting: setSorting
  });

  return (
    <Fragment>
      <div className="d-flex justify-content-between mb-2 align-items-center flex-column flex-sm-row">
        <div className="w-auto">
          <h3>{capitalizeFirstLetter(t('federal_state.title'))}</h3>
        </div>
        <div className="d-flex gap-2 w-auto flex-column flex-sm-row">
          <SearchBox
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
          />
          {/* <ConditionalRender
            condition={checkAccess(
              [modules.FEDERAL_STATE.create],
              userPermission
            )}
          >
            <StandardButton
              buttonText={capitalizeFirstLetter(
                t('federal_state.button.text_1')
              )}
              onClick={() => {
                navigate(FEDERAL_STATE_FORM);
              }}
            />
          </ConditionalRender> */}
        </div>
      </div>
      <ConditionalRender condition={!isLoading} fallBack={<StandardLoader />}>
        <div className="px-4 py-1 px-lg-6 bg-body-emphasis rounded border-top border-bottom border-left border-right border position-relative top-1">
          <AdvanceTableProvider {...table}>
            <AdvanceTable
              tableProps={{
                className: 'phoenix-table fs-9 p-0',
                responsive: true
              }}
              dataLength={allFederalStates.length}
            />
            <ConditionalRender condition={totalPages > 0}>
              <CustomAdvanceTableFooter
                pagination
                totalPages={totalPages}
                totalRecords={totalRecords}
                recordsPerPage={limit}
                currentPage={pageCount}
                setCurrentPage={setPageCount}
                CanPreviousPage={() => pageCount > 1}
                CanNextPage={() => totalPages > pageCount}
                handleNextPage={() => setPageCount(pageCount + 1)}
                handlePreviousPage={() => setPageCount(pageCount - 1)}
                showViewAllBtn={true}
                isAllVisibleData={isAllVisibleData}
                setIsAllVisibleData={setIsAllVisibleData}
                setRecordsPerPage={setLimit}
                isLoading={isLoading}
              />
            </ConditionalRender>
          </AdvanceTableProvider>
        </div>
      </ConditionalRender>
      {renderDeletePopup()}
      <ViewUserModal
        show={viewFederalState.show}
        setShow={value => setViewFederalState({ show: value, data: undefined })}
        title={
          (currentLanguage === ENGLISH
            ? viewFederalState.data?.state_name_en
            : viewFederalState.data?.state_name_de) ?? ''
        }
        icon={<UilUsersAlt size={22} color="#dc3545" />}
        users={viewFederalState.data?.assignedUsers ?? []}
      />
    </Fragment>
  );
};

export default FederalState;
